/**
 * Analytics helper.
 *
 * The site already loads Google Tag Manager (GTM-NHGSK9V6), so events are
 * pushed to the GTM dataLayer rather than shipping a second GA4 snippet. A
 * separate gtag install alongside GTM is the usual cause of doubled pageviews
 * and split reporting.
 *
 * gtag is also called when it happens to be present, so this works whether GA4
 * is wired up inside GTM or loaded directly. Only custom events are sent here,
 * never pageviews, so neither path can double-count.
 *
 * Every call is wrapped: analytics must never be the reason a button stops
 * working. If the tag is blocked, not yet loaded, or missing entirely, these
 * are no-ops.
 */

/** GA4 caps custom string parameters at 100 characters. */
const MAX_PARAM_LENGTH = 100;

function clean(params) {
  const out = {};
  for (const [key, value] of Object.entries(params || {})) {
    if (value === undefined || value === null || value === '') continue;
    out[key] = typeof value === 'string' ? value.slice(0, MAX_PARAM_LENGTH) : value;
  }
  return out;
}

/**
 * Send an event to whichever tag is available.
 *
 * @param {string} name   GA4 event name (snake_case; recommended names reused
 *                        where one fits, e.g. generate_lead, select_content).
 * @param {object} params Event parameters.
 */
export function trackEvent(name, params = {}) {
  if (typeof window === 'undefined' || !name) return;
  const payload = clean(params);

  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: name, ...payload });
  } catch (_) {
    /* tag blocked or unavailable - not worth surfacing */
  }

  try {
    if (typeof window.gtag === 'function') {
      window.gtag('event', name, payload);
    }
  } catch (_) {
    /* as above */
  }
}

/**
 * A call-to-action or navigation click.
 *
 * @param {string} label    What was clicked, e.g. 'Get Started'.
 * @param {string} location Where on the page, e.g. 'hero', 'header', 'footer'.
 * @param {string} [url]    Destination, for outbound links.
 */
export function trackClick(label, location, url) {
  trackEvent('select_content', {
    content_type: 'cta',
    item_id: label,
    link_location: location,
    link_url: url,
    outbound: url ? !url.startsWith('/') && !url.startsWith('#') : undefined,
  });
}

/** An outbound link to a profile or property we do not own. */
export function trackOutbound(label, location, url) {
  trackEvent('click', { link_domain: safeHost(url), link_url: url, link_text: label, link_location: location, outbound: true });
}

function safeHost(url) {
  try {
    return new URL(url, window.location.origin).hostname;
  } catch (_) {
    return undefined;
  }
}
